import { Language } from '@/contexts/LanguageContext';

/**
 * Internal linking rules: map keyword phrases to internal pages.
 * Each rule has Greek + English keywords and the target base path (EN).
 */
interface LinkRule {
  keywordsEl: string[];
  keywordsEn: string[];
  basePath: string; // EN base path, e.g. 'what-is-cro'
  titleEl: string;
  titleEn: string;
}

const LINK_RULES: LinkRule[] = [
  // === Pages close to page one in Search Console (2026-10): linked first ===
  {
    keywordsEl: ['landing page optimization', 'optimize a landing page', 'optimise a landing page', 'landing page checklist'],
    keywordsEn: ['landing page optimization', 'optimize a landing page', 'optimise a landing page', 'landing page checklist'],
    basePath: 'blog/landing-page-optimization-2026-2',
    titleEl: 'Landing Page Optimization',
    titleEn: 'Landing Page Optimization',
  },
  {
    keywordsEl: ['cro audit checklist', 'cro checklist', 'conversion checklist'],
    keywordsEn: ['cro audit checklist', 'cro checklist', 'conversion checklist'],
    basePath: 'cro-checklist',
    titleEl: 'CRO Checklist',
    titleEn: 'CRO Checklist',
  },
  {
    keywordsEl: ['conversion rate benchmarks', 'conversion rate benchmark', 'good conversion rate', 'average conversion rate'],
    keywordsEn: ['conversion rate benchmarks', 'conversion rate benchmark', 'good conversion rate', 'average conversion rate'],
    basePath: 'blog/conversion-rate-benchmarks-truth-34se',
    titleEl: 'Conversion Rate Benchmarks',
    titleEn: 'Conversion Rate Benchmarks',
  },
  {
    keywordsEl: ['statistical significance', 'statistically significant'],
    keywordsEn: ['statistical significance', 'statistically significant'],
    basePath: 'blog/ab-testing-statistical-significance',
    titleEl: 'A/B Test Statistical Significance',
    titleEn: 'A/B Test Statistical Significance',
  },
  {
    keywordsEl: ['a/b test hypothesis', 'test hypothesis', 'hypothesis template'],
    keywordsEn: ['a/b test hypothesis', 'test hypothesis', 'hypothesis template'],
    basePath: 'blog/how-to-write-ab-test-hypothesis',
    titleEl: 'A/B Test Hypothesis',
    titleEn: 'A/B Test Hypothesis',
  },
  {
    keywordsEl: ['prioritization framework', 'ice framework', 'pie framework', 'pxl framework', 'test prioritization'],
    keywordsEn: ['prioritization framework', 'ice framework', 'pie framework', 'pxl framework', 'test prioritization'],
    basePath: 'blog/cro-prioritization-framework-ice-pie-pxl',
    titleEl: 'CRO Prioritization Framework',
    titleEn: 'CRO Prioritization Framework',
  },
  {
    keywordsEl: ['engagement rate', 'engaged sessions', 'bounce rate'],
    keywordsEn: ['engagement rate', 'engaged sessions', 'bounce rate'],
    basePath: 'blog/ga4-engagement-rate-vs-bounce-rate-guide',
    titleEl: 'GA4 Engagement Rate vs Bounce Rate',
    titleEn: 'GA4 Engagement Rate vs Bounce Rate',
  },
  {
    keywordsEl: ['cta button text', 'button text', 'button copy'],
    keywordsEn: ['cta button text', 'button text', 'button copy'],
    basePath: 'blog/cta-button-text-that-converts',
    titleEl: 'CTA Button Text',
    titleEn: 'CTA Button Text',
  },
  {
    keywordsEl: ['free shipping threshold', 'free shipping'],
    keywordsEn: ['free shipping threshold', 'free shipping'],
    basePath: 'blog/free-shipping-threshold-how-to-set',
    titleEl: 'Free Shipping Threshold',
    titleEn: 'Free Shipping Threshold',
  },
  {
    keywordsEl: ['exit intent popups', 'exit intent popup', 'exit-intent popup', 'exit intent'],
    keywordsEn: ['exit intent popups', 'exit intent popup', 'exit-intent popup', 'exit intent'],
    basePath: 'blog/exit-intent-popups-when-they-help',
    titleEl: 'Exit Intent Popups',
    titleEn: 'Exit Intent Popups',
  },
  {
    keywordsEl: ['white-label cro', 'white label cro', 'white-label', 'white label'],
    keywordsEn: ['white-label cro', 'white label cro', 'white-label', 'white label'],
    basePath: 'agencies',
    titleEl: 'White-Label CRO Audits for Agencies',
    titleEn: 'White-Label CRO Audits for Agencies',
  },
  {
    keywordsEl: ['adobe commerce', 'magento'],
    keywordsEn: ['adobe commerce', 'magento'],
    basePath: 'magento-cro-audit',
    titleEl: 'Magento CRO Audit',
    titleEn: 'Magento CRO Audit',
  },
  // === MASTER ARTICLES (cannibalization consolidation 2026) ===
  // High-priority rules pointing to canonical master articles for each topic cluster.
  {
    keywordsEl: ['καλύτερα cro εργαλεία', 'καλύτερα cro tools', 'top cro tools', 'σύγκριση cro εργαλείων', 'best cro tools 2026'],
    keywordsEn: ['best cro tools', 'best cro software', 'top cro tools', 'cro tools comparison', 'compare cro tools', 'cro tools', 'cro software', 'cro tool', 'conversion audit tool'],
    basePath: 'blog/best-cro-tools-comparison-me5n',
    titleEl: 'Καλύτερα CRO Εργαλεία 2026 | Σύγκριση',
    titleEn: 'Best CRO Tools 2026 | Full Comparison',
  },
  {
    keywordsEl: ['ai στρατηγικές cro', 'στρατηγικές ai cro', 'ai conversion rate optimization', 'ai cro στρατηγικές'],
    keywordsEn: ['ai conversion rate optimization', 'ai cro strategies', 'ai-powered cro strategies', 'machine learning conversion optimization', 'ai conversion optimization', 'ai cro', 'machine learning cro'],
    basePath: 'blog/ai-conversion-rate-optimization-strategies-l8n2',
    titleEl: 'AI Στρατηγικές CRO 2026',
    titleEn: 'AI Conversion Rate Optimization Strategies 2026',
  },
  {
    keywordsEl: ['ga4 custom events', 'ga4 παρακολούθηση events', 'custom events ga4', 'οδηγός ga4 events'],
    keywordsEn: ['ga4 custom events', 'ga4 event tracking', 'custom events ga4', 'ga4 events guide', 'google analytics 4 custom events'],
    basePath: 'blog/ga4-custom-events-tracking-guide-4t78',
    titleEl: 'Οδηγός GA4 Custom Events',
    titleEn: 'GA4 Custom Events Tracking Guide',
  },
  {
    keywordsEl: ['τι είναι cro', 'conversion rate optimization', 'βελτιστοποίηση μετατροπών', 'cro ανάλυση', 'cro audit', 'cro στρατηγική'],
    keywordsEn: ['what is cro', 'conversion rate optimization', 'cro analysis', 'cro audit', 'cro strategy'],
    basePath: 'what-is-cro',
    titleEl: 'Τι είναι το CRO',
    titleEn: 'What is CRO',
  },
  {
    keywordsEl: ['user experience', 'εμπειρία χρήστη', 'ux design', 'ux audit', 'ux ανάλυση', 'τι είναι το ux', 'ux βελτιστοποίηση'],
    keywordsEn: ['user experience', 'ux design', 'ux audit', 'ux analysis', 'what is ux', 'ux optimization'],
    basePath: 'what-is-cro',
    titleEl: 'Τι είναι το UX',
    titleEn: 'What is CRO',
  },
  {
    keywordsEl: ['heatmap', 'heatmaps', 'θερμικός χάρτης', 'θερμικοί χάρτες', 'eye tracking', 'attention map', 'χάρτες θερμότητας', 'οπτική προσοχή'],
    keywordsEn: ['heatmap', 'heatmaps', 'heat map', 'heat maps', 'eye tracking', 'attention map', 'attention maps', 'visual attention'],
    basePath: 'features/ai-heatmaps',
    titleEl: 'AI Predictive Attention',
    titleEn: 'AI Predictive Attention',
  },
  {
    keywordsEl: ['neuromarketing', 'νευρομάρκετινγκ', 'cognitive bias', 'γνωστικές προκαταλήψεις', 'ψυχολογία πωλήσεων', 'ψυχολογία μετατροπών'],
    keywordsEn: ['neuromarketing', 'cognitive bias', 'cognitive biases', 'sales psychology', 'persuasion psychology', 'conversion psychology'],
    basePath: 'features/neuromarketing',
    titleEl: 'Neuromarketing',
    titleEn: 'Neuromarketing',
  },
  {
    keywordsEl: ['cro τεχνικές', 'τεχνικές βελτιστοποίησης', 'cro techniques', 'a/b testing', 'split testing', 'τεχνικές cro'],
    keywordsEn: ['cro techniques', 'optimization techniques', 'a/b testing', 'split testing', 'conversion techniques', 'cro methods'],
    basePath: 'features/cro-techniques',
    titleEl: 'CRO Techniques',
    titleEn: 'CRO Techniques',
  },
  {
    keywordsEl: ['δωρεάν ανάλυση', 'δωρεάν cro', 'δοκιμάστε δωρεάν', 'free analysis', 'δωρεάν audit'],
    keywordsEn: ['free analysis', 'free cro analysis', 'try for free', 'free audit', 'free website analysis'],
    basePath: 'free-audit',
    titleEl: 'Δωρεάν CRO Ανάλυση',
    titleEn: 'Free CRO Audit',
  },
  {
    keywordsEl: ['τιμολόγηση', 'τιμές', 'κόστος', 'πλάνα', 'pricing', 'πακέτα'],
    keywordsEn: ['pricing', 'plans', 'cost', 'packages', 'subscription'],
    basePath: 'pricing',
    titleEl: 'Τιμές',
    titleEn: 'Pricing',
  },
  {
    keywordsEl: ['e-commerce', 'eshop', 'e-shop', 'ηλεκτρονικό κατάστημα', 'online store', 'ηλεκτρονικό εμπόριο'],
    keywordsEn: ['e-commerce', 'ecommerce', 'online store', 'e-shop', 'eshop', 'web store'],
    basePath: 'solutions/ecommerce',
    titleEl: 'E-commerce Λύσεις',
    titleEn: 'E-commerce Solutions',
  },
  {
    keywordsEl: ['lead generation', 'leads', 'δημιουργία leads', 'φόρμα επικοινωνίας', 'landing page'],
    keywordsEn: ['lead generation', 'leads', 'contact form', 'landing page', 'lead capture'],
    basePath: 'solutions/lead-generation',
    titleEl: 'Lead Generation',
    titleEn: 'Lead Generation',
  },
  {
    keywordsEl: ['pagespeed', 'ταχύτητα σελίδας', 'page speed', 'core web vitals', 'web vitals', 'loading speed', 'ταχύτητα φόρτωσης'],
    keywordsEn: ['pagespeed', 'page speed', 'core web vitals', 'web vitals', 'loading speed', 'site speed', 'page load time'],
    basePath: 'solutions/technical-analysis',
    titleEl: 'PageSpeed Insights',
    titleEn: 'Technical Audit',
  },
  {
    keywordsEl: ['ανταγωνιστές', 'ανταγωνισμός', 'σύγκριση ανταγωνιστών', 'competitor analysis'],
    keywordsEn: ['competitor', 'competitors', 'competitor analysis', 'competitor comparison', 'competitive analysis'],
    basePath: 'solutions/competitor-comparison',
    titleEl: 'Σύγκριση Ανταγωνιστών',
    titleEn: 'Competitor Comparison',
  },
  {
    keywordsEl: ['πώς λειτουργεί', 'how it works', 'διαδικασία', 'βήματα'],
    keywordsEn: ['how it works', 'process', 'steps', 'workflow'],
    basePath: 'how-it-works',
    titleEl: 'Πώς Λειτουργεί',
    titleEn: 'How It Works',
  },
  {
    keywordsEl: ['ai captain', 'ai βοηθός', 'τεχνητή νοημοσύνη', 'ai assistant'],
    keywordsEn: ['ai captain', 'ai assistant', 'artificial intelligence', 'ai-powered'],
    basePath: 'how-it-works',
    titleEl: 'AI Captain',
    titleEn: 'How It Works',
  },
  // New rules for expanded coverage
  {
    keywordsEl: ['ux audit', 'ux checklist', 'ux έλεγχος', 'ux αξιολόγηση', 'website audit'],
    keywordsEn: ['ux audit', 'ux checklist', 'ux review', 'ux evaluation', 'website audit'],
    basePath: 'free-audit',
    titleEl: 'Οδηγός UX Audit',
    titleEn: 'Free CRO Audit',
  },
  {
    keywordsEl: ['ai cro', 'ai βελτιστοποίηση', 'τεχνητή νοημοσύνη cro'],
    keywordsEn: ['ai optimization'],
    basePath: 'how-it-works',
    titleEl: 'AI CRO',
    titleEn: 'AI-powered CRO',
  },
  {
    keywordsEl: ['bounce rate', 'ποσοστό αποχώρησης', 'εγκατάλειψη καλαθιού', 'cart abandonment'],
    keywordsEn: ['exit rate', 'drop-off rate'],
    basePath: 'ecommerce-cro-guide',
    titleEl: 'Οδηγός E-commerce CRO',
    titleEn: 'E-commerce CRO Guide',
  },
  {
    keywordsEl: ['cro εργαλεία', 'εργαλεία βελτιστοποίησης', 'cro tools', 'cro software'],
    keywordsEn: ['optimization tools', 'conversion tools'],
    basePath: 'what-is-cro',
    titleEl: 'CRO Εργαλεία',
    titleEn: 'CRO Tools',
  },
  {
    keywordsEl: ['ai εργαλεία', 'ai tools', 'εργαλεία τεχνητής νοημοσύνης'],
    keywordsEn: ['ai tools', 'ai software', 'artificial intelligence tools'],
    basePath: 'blog',
    titleEl: 'Οδηγός AI Εργαλείων',
    titleEn: 'EyeCaptain Blog',
  },
  {
    keywordsEl: ['analytics', 'web analytics', 'google analytics', 'ανάλυση δεδομένων'],
    keywordsEn: ['analytics', 'web analytics', 'google analytics', 'data analysis', 'website analytics'],
    basePath: 'blog',
    titleEl: 'Analytics Hub',
    titleEn: 'EyeCaptain Blog',
  },
  {
    keywordsEl: ['website audit', 'έλεγχος ιστοσελίδας', 'ανάλυση ιστοσελίδας', 'website optimization'],
    keywordsEn: ['website audit', 'website analysis', 'website optimization', 'site audit'],
    basePath: 'free-audit',
    titleEl: 'Δωρεάν CRO Audit',
    titleEn: 'Free CRO Audit',
  },
  {
    keywordsEl: ['landing page optimization', 'landing pages', 'σελίδα προορισμού'],
    keywordsEn: ['landing page optimization', 'landing pages', 'landing page'],
    basePath: 'solutions/lead-generation',
    titleEl: 'Lead Generation Website Guide',
    titleEn: 'Lead Generation CRO',
  },
  {
    keywordsEl: ['ga4', 'custom events', 'funnel exploration', 'conversion tracking'],
    keywordsEn: ['ga4', 'custom events', 'funnel exploration', 'conversion tracking'],
    basePath: 'blog',
    titleEl: 'Analytics Hub',
    titleEn: 'EyeCaptain Blog',
  },
];


function getLocalizedUrl(basePath: string, _lang: Language): string {
  // Public site lives at clean root URLs (no language prefix). Emitting /en/...
  // here made every injected link a 301 hop.
  return `/${basePath}`;
}

/** "/en/blog/x/" and "https://eyecaptain.io/blog/x" both become "blog/x". */
function pathKey(href: string): string {
  return href
    .replace(/^https?:\/\/(www\.)?eyecaptain\.io/i, "")
    .replace(/[?#].*$/, "")
    .replace(/^\/(en|el)(?=\/|$)/, "")
    .replace(/^\/+|\/+$/g, "");
}

/** Tags whose text must never get a link: links, headings, code. */
const NO_LINK_TAGS = new Set(["a", "h1", "h2", "h3", "h4", "h5", "h6", "pre", "code", "button"]);

/**
 * Inject internal links into article HTML content.
 * Rules:
 * - Only link each target page ONCE per article (first match)
 * - Never link a page the article already links to, or the article itself
 * - Don't link inside existing <a> tags, headings, or code blocks
 * - Max 5 internal links per article to avoid over-optimization
 */
export function injectInternalLinks(html: string, language: Language, currentPath?: string): string {
  const keywords = language === 'el' ? 'keywordsEl' : 'keywordsEn';
  let linkCount = 0;
  const MAX_LINKS = 5;
  const linkedPaths = new Set<string>();
  for (const m of html.matchAll(/<a\b[^>]*\bhref\s*=\s*["']([^"']+)["']/gi)) linkedPaths.add(pathKey(m[1]));
  if (currentPath) linkedPaths.add(pathKey(currentPath));

  for (const rule of LINK_RULES) {
    if (linkCount >= MAX_LINKS) break;
    if (linkedPaths.has(rule.basePath)) continue;

    // Longer phrases first, so "best cro tools" wins over "cro tools".
    const sorted = [...rule[keywords]].sort((a, b) => b.length - a.length);
    const url = getLocalizedUrl(rule.basePath, language);
    const title = language === 'el' ? rule.titleEl : rule.titleEn;

    for (const kw of sorted) {
      const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const kwRegex = new RegExp(`(?<![\\w-])(${escaped})(?![\\w-])`, 'i');
      let replaced = false;
      const open: string[] = [];

      html = html.replace(/(<[^>]*>)|([^<]+)/g, (_m, tag: string | undefined, text: string | undefined) => {
        if (tag) {
          const t = /^<(\/?)([a-z0-9]+)/i.exec(tag);
          if (t && NO_LINK_TAGS.has(t[2].toLowerCase()) && !/\/>$/.test(tag)) {
            if (t[1]) {
              const at = open.lastIndexOf(t[2].toLowerCase());
              if (at >= 0) open.splice(at, 1);
            } else open.push(t[2].toLowerCase());
          }
          return tag;
        }
        if (replaced || open.length || !text || !kwRegex.test(text)) return text ?? "";
        replaced = true;
        return text.replace(
          kwRegex,
          `<a href="${url}" class="text-primary font-medium border-b border-primary/30 hover:border-primary pb-0.5 transition-all" title="${title}">$1</a>`
        );
      });

      if (replaced) {
        linkedPaths.add(rule.basePath);
        linkCount++;
        break; // Move to next rule
      }
    }
  }

  return html;
}
